import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

/* ============================================================
   Web 演进全景数据 — Web1.0 到 Web5.0
   ============================================================ */
const webEras = [
  {
    ver: 'Web 1.0',
    year: '1991–2004',
    icon: 'ri-file-text-line',
    color: '#A8B5C8',
    tagColor: 'rgba(168,181,200,0.15)',
    title: '静态只读时代',
    subtitle: '信息单向流动',
    desc: 'Web 1.0是互联网的原始形态，由Tim Berners-Lee于1991年发明。页面以静态HTML为主，用户只能"读"，无法"写"。内容由少数网站主控制，超链接是唯一的导航方式。',
    logic: [
      { label: '底层协议', value: 'HTTP/1.0 · TCP/IP · DNS' },
      { label: '核心技术', value: 'HTML · CSS · FTP · 静态服务器' },
      { label: '商业模式', value: '门户广告 · 流量变现' },
      { label: '数据主权', value: '服务器端完全控制' },
    ],
    impact: '雅虎、网景、早期搜索引擎崛起，互联网从军用走向民用，信息民主化第一步。',
    img: 'https://public.readdy.ai/ai/img_res/c2ce402f41498349bd7a489935ad9685.jpg',
  },
  {
    ver: 'Web 2.0',
    year: '2004–2020',
    icon: 'ri-user-shared-line',
    color: '#FFD700',
    tagColor: 'rgba(255,215,0,0.12)',
    title: '社交互动时代',
    subtitle: '用户生成内容UGC',
    desc: 'Web 2.0由Tim O\'Reilly于2004年提出，核心是"参与式网络"。用户从被动接受者变为内容创造者。AJAX技术实现页面动态刷新，社交媒体、视频平台、电商平台爆发式增长。',
    logic: [
      { label: '底层协议', value: 'HTTP/2 · WebSocket · REST API' },
      { label: '核心技术', value: 'AJAX · JavaScript框架 · 云计算 · CDN' },
      { label: '商业模式', value: '数据广告 · 平台经济 · 订阅制' },
      { label: '数据主权', value: '平台垄断用户数据（数据殖民主义）' },
    ],
    impact: 'Facebook、YouTube、Twitter、微信、淘宝崛起。平台经济创造数十万亿市值，但数据隐私问题日益严峻。',
    img: 'https://readdy.ai/api/search-image?query=social%20media%20platforms%20web%202.0%20colorful%20icons%20user%20generated%20content%20community%20network%20vibrant%20colors%20doodle%20art%20style%20dark%20background%20neon%20accents&width=560&height=340&seq=web2-era-001&orientation=landscape',
  },
  {
    ver: 'Web 3.0',
    year: '2020–2026',
    icon: 'ri-links-line',
    color: '#00FF88',
    tagColor: 'rgba(0,255,136,0.12)',
    title: '去中心化时代',
    subtitle: '区块链 · 数字主权',
    desc: 'Web 3.0以区块链为底层基础设施，实现"去信任化"的价值传输。智能合约自动执行协议，NFT赋予数字资产唯一性，DeFi重构金融体系。用户真正拥有自己的数字资产与身份。',
    logic: [
      { label: '底层协议', value: 'Ethereum · Solana · IPFS · Layer2' },
      { label: '核心技术', value: '智能合约 · NFT · DeFi · DAO · 零知识证明' },
      { label: '商业模式', value: 'Token经济 · Play-to-Earn · 协议费用' },
      { label: '数据主权', value: '用户自主控制私钥=控制资产与身份' },
    ],
    impact: 'DeFi锁仓量峰值超2000亿美元，NFT市场年交易额超400亿，元宇宙概念引爆全球资本市场。',
    img: 'https://readdy.ai/api/search-image?query=blockchain%20decentralized%20network%20nodes%20glowing%20green%20neon%20web3%20cryptocurrency%20NFT%20DeFi%20digital%20art%20doodle%20style%20colorful%20dark%20background%20futuristic&width=560&height=340&seq=web3-era-001&orientation=landscape',
  },
  {
    ver: 'Web 4.0',
    year: '2026–2030',
    icon: 'ri-brain-line',
    color: '#FF6B9D',
    tagColor: 'rgba(255,107,157,0.12)',
    title: '智能语义时代',
    subtitle: 'AI驱动 · 万物互联',
    desc: 'Web 4.0是"超级智能网络"，AI大模型成为网络的神经系统，语义理解让机器真正读懂人类意图。物联网设备突破1000亿，数字孪生城市全面落地，虚实边界开始模糊。',
    logic: [
      { label: '底层协议', value: 'Semantic Web · AI Agent协议 · 6G网络切片' },
      { label: '核心技术', value: 'AGI · 数字孪生 · 边缘计算 · 脑机接口初代' },
      { label: '商业模式', value: 'AI即服务 · 数字孪生订阅 · 意图经济' },
      { label: '数据主权', value: 'AI代理代表用户自主决策与交互' },
    ],
    impact: '预计2028年全球AI市场规模超15万亿美元，数字孪生城市覆盖全球TOP100城市，元宇宙日活用户突破10亿。',
    img: 'https://readdy.ai/api/search-image?query=AI%20artificial%20intelligence%20semantic%20web%20smart%20city%20digital%20twin%20holographic%20interface%20colorful%20doodle%20art%20style%20pink%20neon%20glow%20dark%20background%20futuristic%202028&width=560&height=340&seq=web4-era-001&orientation=landscape',
  },
  {
    ver: 'Web 5.0',
    year: '2030+',
    icon: 'ri-global-line',
    color: '#00FFCC',
    tagColor: 'rgba(0,255,204,0.12)',
    title: '虚实融合时代',
    subtitle: '数据主权 · 意识互联',
    desc: 'Web 5.0是Jack Dorsey提出的"去中心化网络+数字身份主权"终极形态。结合脑机接口、量子通信与全息投影，人类将在虚实完全融合的空间中生活、工作与社交。',
    logic: [
      { label: '底层协议', value: '量子通信协议 · 脑机接口标准 · 去中心化身份DID' },
      { label: '核心技术', value: '量子计算 · 脑机接口 · 裸眼3D · 全息投影' },
      { label: '商业模式', value: '意识经济 · 虚实资产互通 · 数字永生服务' },
      { label: '数据主权', value: '个人完全掌控数字身份与意识数据' },
    ],
    impact: '预计2035年全球元宇宙经济规模超80万亿美元，虚拟世界GDP超越多数实体国家。',
    img: 'https://readdy.ai/api/search-image?query=web5%20metaverse%20virtual%20reality%20brain%20computer%20interface%20quantum%20holographic%20world%20colorful%20doodle%20art%20style%20cyan%20neon%20glow%20dark%20background%20futuristic%202030&width=560&height=340&seq=web5-era-001&orientation=landscape',
  },
];

/* ============================================================
   6G / 7G 底层逻辑详解
   ============================================================ */
const networkGens = [
  {
    gen: '5G',
    year: '2019–2025',
    color: '#A8B5C8',
    speed: '10 Gbps',
    latency: '1ms',
    density: '100万/km²',
    title: '万物互联基础',
    desc: '5G实现了移动互联网从"人联"到"物联"的跨越。eMBB（增强移动宽带）、mMTC（海量机器通信）、uRLLC（超可靠低延迟）三大场景奠定了物联网基础，但仍无法满足元宇宙的实时渲染需求。',
    bottleneck: '频谱效率瓶颈 · 基站密度不足 · 能耗过高 · 无法支撑全息通信',
    icon: 'ri-signal-tower-line',
  },
  {
    gen: '6G',
    year: '2025–2030',
    color: '#00FF88',
    speed: '1 Tbps',
    latency: '<0.1ms',
    density: '1000万/km²',
    title: '元宇宙基础设施',
    desc: '6G工作在太赫兹（THz）频段，峰值速率达1Tbps，是5G的100倍。空天地海一体化网络实现全球无缝覆盖。AI原生网络架构让基站具备自主学习能力，网络切片技术为元宇宙提供专属QoS保障。',
    bottleneck: '太赫兹信号穿透力弱 · 基站建设成本高 · 全球频谱协调难度大',
    keyTech: ['太赫兹通信（0.1-10THz）', 'AI原生网络架构', '空天地海一体化', '智能超表面RIS', '语义通信'],
    icon: 'ri-signal-tower-fill',
    highlight: true,
  },
  {
    gen: '7G',
    year: '2030–2035',
    color: '#FF6B9D',
    speed: '10 Tbps',
    latency: '<0.01ms',
    density: '1亿/km²',
    title: '意识级通信网络',
    desc: '7G将实现"意识级"通信——传输的不再是比特流，而是人类感知与意识的数字映射。量子通信与经典通信融合，脑机接口标准化接入，全息投影实时传输成为现实。神经网络直接与互联网交互。',
    bottleneck: '量子纠错技术尚未成熟 · 脑机接口伦理争议 · 意识数据安全',
    keyTech: ['量子-经典混合通信', '脑机接口标准协议', '全息实时传输', '意识数字化编码', '零延迟感知网络'],
    icon: 'ri-brain-line',
  },
];

export default function NetworkEvolution() {
  const [activeWeb, setActiveWeb] = useState(2); // 默认Web3.0
  const [activeGen, setActiveGen] = useState(1); // 默认6G
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: webRef, isVisible: webVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: netRef, isVisible: netVisible } = useScrollAnimation({ threshold: 0.05 });

  const currentWeb = webEras[activeWeb];
  const currentGen = networkGens[activeGen];

  return (
    <section id="web" className="relative py-24 md:py-32 bg-[#020810] overflow-hidden doodle-dots-bg">
      {/* 涂鸦彩色斑点背景装饰 */}
      <div className="doodle-splat splat-yellow absolute top-20 right-10 w-64 h-64" />
      <div className="doodle-splat splat-pink absolute bottom-40 left-10 w-48 h-48" />
      <div className="doodle-splat splat-cyan absolute top-1/2 right-1/4 w-32 h-32" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">

        {/* ===== Header ===== */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="section-badge mb-6 mx-auto w-fit">
            <i className="ri-git-branch-line" />
            Technology Evolution
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            WEB 演进
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#00FFCC]"> 全景图</span>
          </h2>
          <p className="text-white/40 text-base max-w-2xl mx-auto leading-relaxed">
            从静态只读到意识互联，从5G到7G——深度解析互联网与通信网络的底层逻辑与演化进程
          </p>
          {/* 涂鸦装饰线 */}
          <div className="doodle-wave-divider mt-8 opacity-60" />
          <div className="mt-6">
            <Link
              to="/network"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ background: 'rgba(0,255,136,0.12)', border: '1.5px solid rgba(0,255,136,0.4)', color: '#00FF88' }}
            >
              <i className="ri-arrow-right-circle-line" />
              深度解析：Web演进全景 &amp; 5G→7G底层逻辑
              <i className="ri-external-link-line text-xs opacity-60" />
            </Link>
          </div>
        </div>

        {/* ===== Web 版本选择器 ===== */}
        <div
          ref={webRef as React.RefObject<HTMLDivElement>}
          className={`transition-all duration-700 delay-100 ${webVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Tab 选择器 */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {webEras.map((w, i) => (
              <button
                key={w.ver}
                onClick={() => setActiveWeb(i)}
                className={`px-5 py-2.5 rounded-full border text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeWeb === i
                    ? 'text-black font-bold'
                    : 'border-white/15 text-white/40 hover:border-white/30 hover:text-white/70'
                }`}
                style={activeWeb === i ? {
                  backgroundColor: w.color,
                  borderColor: w.color,
                  boxShadow: `0 0 20px ${w.color}40`,
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                } : {}}
              >
                {w.ver}
                <span className="ml-2 text-xs opacity-60">{w.year.split('–')[0]}</span>
              </button>
            ))}
          </div>

          {/* Web 详情卡片 */}
          <div className="flex flex-col lg:flex-row gap-8 mb-6">
            {/* 左侧图片 */}
            <div className="flex-1 relative rounded-2xl overflow-hidden border border-white/8 hover:border-white/20 transition-all duration-300 doodle-hover-card" style={{ minHeight: '300px' }}>
              <img
                src={currentWeb.img}
                alt={currentWeb.title}
                className="w-full h-full object-cover object-top"
                style={{ minHeight: '300px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020810]/90 via-[#020810]/30 to-transparent" />
              <div className="absolute top-4 left-4">
                <span
                  className="px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: currentWeb.tagColor, color: currentWeb.color, border: `1.5px solid ${currentWeb.color}60` }}
                >
                  {currentWeb.ver} · {currentWeb.year}
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-white text-2xl font-black mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{currentWeb.title}</h3>
                <p className="text-sm font-semibold" style={{ color: currentWeb.color }}>{currentWeb.subtitle}</p>
              </div>
            </div>

            {/* 右侧详情 */}
            <div className="flex-1 flex flex-col gap-5">
              {/* 描述 */}
              <div className="p-5 rounded-2xl bg-white/3 border border-white/8">
                <p className="text-white/70 leading-relaxed text-sm">{currentWeb.desc}</p>
              </div>

              {/* 底层逻辑 */}
              <div className="p-5 rounded-2xl bg-white/3 border border-white/8">
                <h4 className="text-white text-sm font-bold mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center doodle-icon-wrap" style={{ backgroundColor: `${currentWeb.color}20` }}>
                    <i className="ri-cpu-line text-xs" style={{ color: currentWeb.color }} />
                  </div>
                  底层逻辑解析
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentWeb.logic.map((l) => (
                    <div key={l.label} className="flex flex-col gap-1 p-3 rounded-xl bg-black/30 border border-white/5">
                      <span className="text-white/40 text-xs">{l.label}</span>
                      <span className="text-white/80 text-xs font-semibold leading-relaxed">{l.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 历史影响 */}
              <div className="doodle-quote text-white/60 text-sm">
                <strong style={{ color: currentWeb.color }}>历史影响：</strong>{currentWeb.impact}
              </div>
            </div>
          </div>

          {/* Web 演进时间轴 */}
          <div className="relative mt-12 mb-20">
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white/10 to-transparent hidden md:block" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {webEras.map((w, i) => (
                <button
                  key={w.ver}
                  onClick={() => setActiveWeb(i)}
                  className={`relative flex flex-col items-center text-center gap-3 p-4 rounded-2xl border transition-all duration-300 cursor-pointer doodle-hover-card ${
                    activeWeb === i ? 'border-opacity-60' : 'border-white/8 bg-white/2 hover:border-white/20'
                  }`}
                  style={activeWeb === i ? {
                    borderColor: `${w.color}60`,
                    backgroundColor: `${w.color}08`,
                  } : {}}
                >
                  {/* 时间轴节点 */}
                  <div
                    className="w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all duration-300"
                    style={activeWeb === i ? {
                      backgroundColor: w.color,
                      borderColor: w.color,
                      boxShadow: `0 0 12px ${w.color}80`,
                    } : { backgroundColor: '#020810', borderColor: 'rgba(255,255,255,0.2)' }}
                  />
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl doodle-icon-wrap"
                    style={{ backgroundColor: activeWeb === i ? `${w.color}20` : 'rgba(255,255,255,0.04)' }}
                  >
                    <i className={`${w.icon} text-lg`} style={{ color: activeWeb === i ? w.color : 'rgba(255,255,255,0.3)' }} />
                  </div>
                  <div>
                    <p className="font-black text-sm" style={{ fontFamily: 'Orbitron, sans-serif', color: activeWeb === i ? w.color : 'rgba(255,255,255,0.7)' }}>{w.ver}</p>
                    <p className="text-white/30 text-xs mt-0.5">{w.year}</p>
                    <p className="text-white/40 text-xs mt-1.5 leading-relaxed">{w.subtitle}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ===== 6G / 7G 底层逻辑详解 ===== */}
        <div
          ref={netRef as React.RefObject<HTMLDivElement>}
          className={`border-t border-white/8 pt-16 transition-all duration-700 delay-200 ${netVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="text-center mb-12">
            <div className="section-badge mb-4 mx-auto w-fit">
              <i className="ri-signal-tower-line" />
              Network Generation
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              5G → 6G → 7G
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#FF6B9D]"> 底层逻辑</span>
            </h3>
            <p className="text-white/40 text-sm max-w-xl mx-auto">从毫米波到太赫兹，从比特传输到意识传输——通信网络的三次质变</p>
          </div>

          {/* 代际选择器 */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            {networkGens.map((g, i) => (
              <button
                key={g.gen}
                onClick={() => setActiveGen(i)}
                className={`flex items-center gap-3 px-6 py-3 rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeGen === i ? 'text-black font-bold' : 'border-white/15 text-white/40 hover:border-white/30 hover:text-white/70'
                }`}
                style={activeGen === i ? {
                  backgroundColor: g.color,
                  borderColor: g.color,
                  boxShadow: `0 0 20px ${g.color}40`,
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                } : {}}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${g.icon} text-sm`} />
                </div>
                <span className="text-base font-black" style={{ fontFamily: 'Orbitron, sans-serif' }}>{g.gen}</span>
                <span className="text-xs opacity-70">{g.year}</span>
              </button>
            ))}
          </div>

          {/* 代际详情 */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* 左侧核心指标 */}
            <div className="lg:w-72 flex flex-col gap-4">
              <div className="p-6 rounded-2xl border doodle-hover-card" style={{ borderColor: `${currentGen.color}30`, backgroundColor: `${currentGen.color}06` }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl doodle-icon-wrap" style={{ backgroundColor: `${currentGen.color}20` }}>
                    <i className={`${currentGen.icon} text-2xl`} style={{ color: currentGen.color }} />
                  </div>
                  <div>
                    <p className="text-white font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif', color: currentGen.color }}>{currentGen.gen}</p>
                    <p className="text-white/40 text-xs">{currentGen.year}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  {[
                    { label: '峰值速率', value: currentGen.speed, icon: 'ri-speed-line' },
                    { label: '端到端延迟', value: currentGen.latency, icon: 'ri-timer-flash-line' },
                    { label: '连接密度', value: currentGen.density, icon: 'ri-router-line' },
                  ].map((m) => (
                    <div key={m.label} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${currentGen.color}15` }}>
                        <i className={`${m.icon} text-sm`} style={{ color: currentGen.color }} />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs">{m.label}</p>
                        <p className="font-black text-base" style={{ fontFamily: 'Orbitron, sans-serif', color: currentGen.color }}>{m.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 瓶颈 */}
              <div className="p-4 rounded-2xl bg-white/3 border border-white/8">
                <p className="text-white/40 text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line text-yellow-400" />
                  当前瓶颈
                </p>
                <p className="text-white/60 text-xs leading-relaxed">{currentGen.bottleneck}</p>
              </div>
            </div>

            {/* 右侧详情 */}
            <div className="flex-1 flex flex-col gap-5">
              <div>
                <h4 className="text-white text-xl font-bold mb-1">{currentGen.title}</h4>
                <p className="text-white/60 leading-relaxed text-sm">{currentGen.desc}</p>
              </div>

              {/* 关键技术 */}
              {currentGen.keyTech && (
                <div>
                  <p className="text-white/40 text-xs font-semibold mb-3 flex items-center gap-1.5">
                    <i className="ri-flashlight-line" style={{ color: currentGen.color }} />
                    核心关键技术
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {currentGen.keyTech.map((t) => (
                      <span
                        key={t}
                        className="px-3 py-1.5 text-xs font-semibold rounded-full border"
                        style={{
                          borderColor: `${currentGen.color}40`,
                          color: currentGen.color,
                          backgroundColor: `${currentGen.color}08`,
                          borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 与元宇宙的关系 */}
              <div className="p-5 rounded-2xl bg-white/3 border border-white/8">
                <p className="text-white/40 text-xs font-semibold mb-3 flex items-center gap-1.5">
                  <i className="ri-vip-diamond-line" style={{ color: currentGen.color }} />
                  与元宇宙的关系
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      label: '支撑能力',
                      value: currentGen.gen === '5G' ? '基础物联网 · 低清XR' : currentGen.gen === '6G' ? '全息通信 · 实时渲染' : '意识互联 · 完全沉浸',
                    },
                    {
                      label: '元宇宙阶段',
                      value: currentGen.gen === '5G' ? 'Web3.0初期' : currentGen.gen === '6G' ? 'Web4.0爆发期' : 'Web5.0终极形态',
                    },
                    {
                      label: '用户体验',
                      value: currentGen.gen === '5G' ? '低分辨率VR' : currentGen.gen === '6G' ? '8K全息投影' : '意识级沉浸',
                    },
                  ].map((r) => (
                    <div key={r.label} className="p-3 rounded-xl bg-black/30 border border-white/5 text-center">
                      <p className="text-white/40 text-xs mb-1">{r.label}</p>
                      <p className="text-white/80 text-xs font-semibold leading-relaxed">{r.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5G→6G→7G 演进对比 */}
              <div className="p-5 rounded-2xl bg-white/3 border border-white/8">
                <p className="text-white/40 text-xs font-semibold mb-4">三代网络核心指标对比</p>
                <div className="flex flex-col gap-3">
                  {[
                    { label: '峰值速率', vals: ['10 Gbps', '1 Tbps', '10 Tbps'], colors: ['#A8B5C8', '#00FF88', '#FF6B9D'] },
                    { label: '端到端延迟', vals: ['1ms', '<0.1ms', '<0.01ms'], colors: ['#A8B5C8', '#00FF88', '#FF6B9D'] },
                    { label: '频谱范围', vals: ['毫米波 mmWave', '太赫兹 THz', '量子+THz'], colors: ['#A8B5C8', '#00FF88', '#FF6B9D'] },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3">
                      <span className="text-white/30 text-xs w-20 flex-shrink-0">{row.label}</span>
                      <div className="flex-1 flex gap-2">
                        {row.vals.map((v, vi) => (
                          <div
                            key={vi}
                            className="flex-1 text-center py-1.5 px-2 rounded-lg text-xs font-semibold"
                            style={{ backgroundColor: `${row.colors[vi]}12`, color: row.colors[vi], border: `1px solid ${row.colors[vi]}25` }}
                          >
                            {v}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
